import { useEffect, useState, type ChangeEvent } from "react";
import { Card } from "@pericles/framework-frontend";

import { fetchProjectDocuments } from "../api/analysisClient";
import { uploadProjectDocumentToProject } from "../api/projectDocumentsClient";
import {
  analyzeProjectDocumentPdf,
  getPdfAnalysis,
  type PdfAnalysisMode,
  type PdfAnalysisResult,
} from "../api/pdfAnalysisClient";
import type { ProjectDocument } from "../types/documents";

import { InlineAlert } from "./InlineAlert";

function extractPdfAnalysisResult(doc: ProjectDocument): PdfAnalysisResult | undefined {
  const outputs = doc.analysis?.outputs;

  if (!outputs) return undefined;

  return {
    mode: "TEXT",
    summary: outputs.summary ?? doc.analysis?.initialSummary ?? "",
    keyPoints: outputs.keyFindings ?? [],
    risks: outputs.risks ?? outputs.openQuestions ?? [],
    nextSteps: outputs.suggestedActions ?? [],
  };
}

interface ProjectDiscoveryDocumentsPanelProps {
  projectId: string;
  refreshKey?: number;
}

export function ProjectDiscoveryDocumentsPanel({
  projectId,
  refreshKey,
}: ProjectDiscoveryDocumentsPanelProps): JSX.Element {
  const [documents, setDocuments] = useState<ProjectDocument[]>([]);
  const [loadingDocuments, setLoadingDocuments] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [pdfAnalysisState, setPdfAnalysisState] = useState<
    Record<
      string,
      { loading: boolean; error: string | null; result?: PdfAnalysisResult; analysisMode: PdfAnalysisMode }
    >
  >({});
  const [analysisContextByDoc, setAnalysisContextByDoc] = useState<Record<string, string>>({});
  const [uploading, setUploading] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadTitle, setUploadTitle] = useState<string>("");
  const [uploadContext, setUploadContext] = useState<string>("");
  const [fileInputKey, setFileInputKey] = useState<number>(0);

  useEffect(() => {
    void loadDocuments();
  }, [projectId, refreshKey]);

  const loadDocuments = async () => {
    if (!projectId) return;

    setLoadingDocuments(true);
    setError(null);

    try {
      const result = await fetchProjectDocuments(projectId);
      setDocuments(result);
      const analyses = await Promise.all(
        result.map((doc) =>
          getPdfAnalysis(projectId, doc.id).catch((error) => {
            // eslint-disable-next-line no-console
            console.warn("Failed to load stored PDF analysis", { projectId, documentId: doc.id, error });
            return null;
          }),
        ),
      );

      setPdfAnalysisState((current) => {
        const next: Record<
          string,
          { loading: boolean; error: string | null; result?: PdfAnalysisResult; analysisMode: PdfAnalysisMode }
        > = {};

        result.forEach((doc, index) => {
          const existing = current[doc.id];
          const persisted = analyses[index] ?? undefined;
          next[doc.id] = {
            loading: false,
            error: null,
            result: persisted ?? existing?.result ?? extractPdfAnalysisResult(doc),
            analysisMode: existing?.analysisMode ?? "TEXT",
          };
        });

        return next;
      });
      setAnalysisContextByDoc((current) => {
        const next: Record<string, string> = {};
        result.forEach((doc) => {
          next[doc.id] = current[doc.id] ?? "";
        });
        return next;
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load documents";
      setError(message);
    } finally {
      setLoadingDocuments(false);
    }
  };

  const handlePdfAnalysisContextChange = (documentId: string, userContext: string) => {
    setAnalysisContextByDoc((current) => ({
      ...current,
      [documentId]: userContext,
    }));
  };

  const handlePdfAnalysisModeChange = (documentId: string, analysisMode: PdfAnalysisMode) => {
    setPdfAnalysisState((current) => ({
      ...current,
      [documentId]: {
        loading: current[documentId]?.loading ?? false,
        error: current[documentId]?.error ?? null,
        result: current[documentId]?.result,
        analysisMode,
      },
    }));
  };

  const handleAnalyzePdf = async (documentId: string) => {
    if (!projectId) {
      setPdfAnalysisState((current) => ({
        ...current,
        [documentId]: {
          loading: false,
          error: "Projet manquant : impossible d'analyser le PDF.",
          result: current[documentId]?.result,
          analysisMode: current[documentId]?.analysisMode ?? "TEXT",
        },
      }));
      return;
    }

    setPdfAnalysisState((current) => ({
      ...current,
      [documentId]: {
        loading: true,
        error: null,
        result: current[documentId]?.result,
        analysisMode: current[documentId]?.analysisMode ?? "TEXT",
      },
    }));

    try {
      const userContext = (analysisContextByDoc[documentId] ?? "").trim();
      const result = await analyzeProjectDocumentPdf(projectId, documentId, {
        userContext: userContext || undefined,
        mode: pdfAnalysisState[documentId]?.analysisMode ?? "TEXT",
      });

      setPdfAnalysisState((current) => ({
        ...current,
        [documentId]: {
          loading: false,
          error: null,
          result,
          analysisMode: pdfAnalysisState[documentId]?.analysisMode ?? "TEXT",
        },
      }));
      await loadDocuments();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Échec de l'analyse du PDF";
      setPdfAnalysisState((current) => ({
        ...current,
        [documentId]: {
          loading: false,
          error: message,
          result: current[documentId]?.result,
          analysisMode: current[documentId]?.analysisMode ?? "TEXT",
        },
      }));
    }
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    setUploadFile(file);
    setUploadError(null);

    if (file && !uploadTitle.trim()) {
      setUploadTitle(file.name);
    }
  };

  const resetUploadForm = () => {
    setUploadFile(null);
    setUploadTitle("");
    setUploadContext("");
    setFileInputKey((current) => current + 1);
  };

  const handleUpload = async () => {
    if (!projectId) {
      setUploadError("Projet manquant : impossible d'uploader un document.");
      return;
    }

    if (!uploadFile) {
      setUploadError("Sélectionnez un fichier PDF à envoyer.");
      return;
    }

    const effectiveTitle = uploadTitle.trim() || uploadFile.name;

    if (!effectiveTitle.trim()) {
      setUploadError("Renseignez un titre pour le document.");
      return;
    }

    setUploading(true);
    setUploadError(null);

    try {
      await uploadProjectDocumentToProject(projectId, uploadFile, {
        title: effectiveTitle,
        context: uploadContext,
      });

      resetUploadForm();
      await loadDocuments();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Échec du téléversement du document";
      setUploadError(message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <Card title="Documents">
        <div className="flex flex-wrap items-center justify-between gap-2 pb-2 text-sm text-slate-700">
          <p className="text-sm text-slate-700">Liste des documents du projet et analyse IA des PDF.</p>
          <button type="button" className="button button-secondary button-sm" onClick={() => void loadDocuments()}>
            {loadingDocuments ? "Refreshing…" : "Refresh documents"}
          </button>
        </div>

        {error ? <InlineAlert tone="danger" title={error} /> : null}

        <div className="mb-4 rounded border border-slate-200 bg-slate-50 p-3 text-sm text-slate-800">
          <div className="mb-2 text-slate-700">Ajouter un document PDF avec titre et contexte facultatif.</div>

          {uploadError ? <InlineAlert tone="danger" title={uploadError} /> : null}

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <label className="flex flex-col gap-1 text-xs uppercase tracking-wide text-slate-600">
              <span>Document PDF</span>
              <input
                key={fileInputKey}
                type="file"
                accept="application/pdf"
                className="input"
                onChange={handleFileChange}
              />
              <span className="text-[11px] normal-case text-slate-500">Seuls les fichiers PDF sont pris en charge.</span>
            </label>

            <label className="flex flex-col gap-1 text-xs uppercase tracking-wide text-slate-600">
              <span>Titre</span>
              <input
                type="text"
                className="input"
                placeholder="Titre du document"
                value={uploadTitle}
                onChange={(event) => setUploadTitle(event.target.value)}
              />
              <span className="text-[11px] normal-case text-slate-500">Par défaut : nom du fichier PDF.</span>
            </label>
          </div>

          <label className="mt-3 flex flex-col gap-1 text-xs uppercase tracking-wide text-slate-600">
            <span>Context (optional)</span>
            <textarea
              className="input"
              rows={3}
              placeholder="Contexte utilisateur, attentes, contraintes…"
              value={uploadContext}
              onChange={(event) => setUploadContext(event.target.value)}
            />
          </label>

          <div className="mt-3 flex justify-end">
            <button
              type="button"
              className="button button-primary"
              disabled={uploading || !uploadFile || !(uploadTitle.trim() || uploadFile?.name)}
              onClick={() => void handleUpload()}
            >
              {uploading ? "Uploading…" : "Ajouter à la discovery"}
            </button>
          </div>
        </div>

        {loadingDocuments ? <p className="text-sm text-slate-600">Loading documents…</p> : null}

        {documents.length === 0 && !loadingDocuments ? (
          <div className="flex flex-col gap-3 rounded border border-dashed border-slate-300 bg-white p-4 text-sm text-slate-700">
            <p className="text-slate-700">Importez vos premiers supports (PDF) pour lancer l'analyse IA.</p>
            <p className="text-slate-600">Chaque document peut inclure un titre personnalisé et un contexte pour guider l'assistant.</p>
          </div>
        ) : null}

        <ul className="flex flex-col gap-3">
          {documents.map((doc) => (
            <li key={doc.id} className="rounded border border-slate-200 p-3 text-sm text-slate-800">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-semibold">{doc.title ?? doc.filename}</span>
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <a
                    href={`/api/documents/${encodeURIComponent(doc.id)}/file`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-indigo-600 hover:text-indigo-700"
                  >
                    View
                  </a>
                  <span>
                    {doc.mimeType} · {(doc.size / 1024).toFixed(1)} KB
                  </span>
                </div>
              </div>

              <div className="text-xs text-slate-600">Text extraction: {doc.textExtraction?.status ?? "pending"}</div>

              {doc.mimeType?.toLowerCase().includes("pdf") || doc.filename.toLowerCase().endsWith(".pdf") ? (
                <div className="mt-3 rounded border border-indigo-100 bg-indigo-50 p-3 text-xs text-slate-700">
                  <div className="flex flex-col gap-2">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="font-semibold text-slate-800">Analyse IA du PDF</span>
                    <button
                      type="button"
                      className="button button-primary button-xs"
                        onClick={() => void handleAnalyzePdf(doc.id)}
                        disabled={pdfAnalysisState[doc.id]?.loading === true}
                      >
                        {pdfAnalysisState[doc.id]?.loading ? "Analyse en cours…" : "Analyze by AI"}
                      </button>
                    </div>

                    <div className="flex flex-wrap items-center gap-3 text-[13px] text-slate-700">
                      <span>Mode d'analyse :</span>
                      <label className="flex items-center gap-1">
                        <input
                          type="radio"
                          name={`analysis-mode-${doc.id}`}
                          value="TEXT"
                          checked={(pdfAnalysisState[doc.id]?.analysisMode ?? "TEXT") === "TEXT"}
                          onChange={() => handlePdfAnalysisModeChange(doc.id, "TEXT")}
                        />
                        Texte (par défaut)
                      </label>
                      <label className="flex items-center gap-1">
                        <input
                          type="radio"
                          name={`analysis-mode-${doc.id}`}
                          value="IMAGE"
                          checked={(pdfAnalysisState[doc.id]?.analysisMode ?? "TEXT") === "IMAGE"}
                          onChange={() => handlePdfAnalysisModeChange(doc.id, "IMAGE")}
                        />
                        Image (scan / slides)
                      </label>
                    </div>

                    <label className="flex flex-col gap-1 text-[13px] text-slate-700">
                      <span>Context for AI (optional)</span>
                      <textarea
                        className="input"
                        rows={2}
                        placeholder="Objectifs, cas d'usage, points d'attention…"
                        value={analysisContextByDoc[doc.id] ?? ""}
                        onChange={(event) => handlePdfAnalysisContextChange(doc.id, event.target.value)}
                      />
                    </label>

                    {pdfAnalysisState[doc.id]?.error ? (
                      <InlineAlert tone="danger" title={pdfAnalysisState[doc.id]?.error ?? "Erreur"} />
                    ) : null}

                    {pdfAnalysisState[doc.id]?.result ? (
                      <div className="flex flex-col gap-2 rounded border border-slate-200 bg-white p-2 text-left text-slate-800">
                        <div>
                          <div className="text-[11px] uppercase tracking-wide text-slate-500">Résumé</div>
                          <p className="text-sm">{pdfAnalysisState[doc.id]?.result?.summary}</p>
                        </div>
                        <div>
                          <div className="text-[11px] uppercase tracking-wide text-slate-500">Points clés</div>
                          <ul className="list-disc pl-5 text-sm">
                            {pdfAnalysisState[doc.id]?.result?.keyPoints.map((point, index) => (
                              <li key={index}>{point}</li>
                            ))}
                          </ul>
                        </div>
                        <div>
                          <div className="text-[11px] uppercase tracking-wide text-slate-500">Risques ou questions ouvertes</div>
                          <ul className="list-disc pl-5 text-sm">
                            {(pdfAnalysisState[doc.id]?.result?.risks ?? []).map((risk, index) => (
                              <li key={index}>{risk}</li>
                            ))}
                          </ul>
                        </div>
                        <div>
                          <div className="text-[11px] uppercase tracking-wide text-slate-500">Prochaines étapes suggérées</div>
                          <ul className="list-disc pl-5 text-sm">
                            {(pdfAnalysisState[doc.id]?.result?.nextSteps ?? []).map((step, index) => (
                              <li key={index}>{step}</li>
                            ))}
                          </ul>
                        </div>
                        {pdfAnalysisState[doc.id]?.result?.modelInfo ? (
                          <div className="text-[11px] uppercase tracking-wide text-slate-500">
                            Modèle: {pdfAnalysisState[doc.id]?.result?.modelInfo}
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}
